import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { palette } from 'theme';
import _ from 'lodash';

import { updateOrderAddress } from 'features/checkout/actions';

import RadioGroup from '@material-ui/core/RadioGroup';
import FormControl from '@material-ui/core/FormControl';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';

import CircularLoader from 'components/CircularLoader';
import OrderAddressDisplay from 'components/OrderAddressDisplay';

import AddressForm from './AddressForm';
import { CheckoutFormControlLabel, CheckoutCollapse } from './CheckoutRadio';

import {
  getBillingAddresses,
  getShippingAddresses,
  getLastBillingAddress,
  getLastShippingAddress
} from 'features/buckaneer/actions';

const AddressDetail = styled.div`
  padding-bottom: 14px;
`;

const NewAddressLabel = styled.strong`
  color: ${palette.primary.main};
`;

const NextButton = styled(Button)`
  margin-top: 24px;
`;

const AddressFormControlLabel = ({ address, label, ...other }) => {
  const shortLabel = `${address.company_name} - ${address.first_last} (${address.notification_email})`;
  const longLabel = ` ${address.street_address_1} ${address.street_address_2}, ${address.city_state_zip}, ${address.country_name}`;
  return (
    <>
      {address && (
        <>
          <CheckoutFormControlLabel
            label={
              label ? (
                label
              ) : (
                <>
                  <strong>{shortLabel}</strong> {!other.checked && longLabel}
                </>
              )
            }
            withCollapse
            {...other}
          />
          <CheckoutCollapse in={other.checked}>
            <AddressDetail>
              <OrderAddressDisplay {...address} />
            </AddressDetail>
          </CheckoutCollapse>
        </>
      )}
    </>
  );
};

const AddressSelector = ({
  type,
  order,
  activeStep,
  buckLoading,
  checkoutLoading,
  billingAddresses,
  shippingAddresses,
  lastBillingAddress,
  lastShippingAddress,
  getBillingAddresses,
  getShippingAddresses,
  getLastShippingAddress,
  getLastBillingAddress,
  updateOrderAddress
}) => {
  // Get addresses on load
  useEffect(
    () => {
      if (type === 'shipping') {
        getShippingAddresses();
        getLastShippingAddress();
      }
      if (type === 'billing') {
        getBillingAddresses();
        getLastBillingAddress();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  let defaultAddressId;
  let orderAddress;
  let addresses;

  if (type === 'shipping') {
    orderAddress = order.shipping_address;
    // Set the address to be selected on load, order address, last shipping address or add new
    defaultAddressId = String(
      _.get(orderAddress, 'id', _.get(lastShippingAddress, 'id', 'new'))
    );
    addresses = shippingAddresses;
  }
  if (type === 'billing') {
    orderAddress = order.billing_address;
    // Set the address to be selected on load, order address, last billing address, the shipping address or add new
    defaultAddressId = String(
      _.get(
        orderAddress,
        'id',
        _.get(
          order.shipping_address,
          'id',
          _.get(lastBillingAddress, 'id', 'new')
        )
      )
    );
    addresses = billingAddresses;
  }

  // If the user does not have the order address in their addresses we'll need to display it separately later
  const userHasOrderAddress = _.some(addresses, orderAddress);

  const [selectedAddressOption, setSelectedAddressOption] = useState(
    defaultAddressId
  );

  // We need to set the selected address if the default address updates
  useEffect(() => {
    setSelectedAddressOption(defaultAddressId);
  }, [defaultAddressId]);

  const handleChange = event => {
    setSelectedAddressOption(String(event.target.value));
  };

  const goToNext = () => {
    updateOrderAddress(
      order.token,
      `${type}_address`,
      selectedAddressOption,
      null
    );
  };

  return (buckLoading || checkoutLoading) && !order ? (
    <CircularLoader />
  ) : (
    <>
      {order && (
        <Grid container>
          <Grid item xs={12}>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                id={`${type}-radiogroup`}
                value={selectedAddressOption}
                onChange={handleChange}
              >
                {!userHasOrderAddress && orderAddress && (
                  <AddressFormControlLabel
                    value={String(orderAddress.id)}
                    address={orderAddress}
                    key={orderAddress.id}
                  />
                )}
                {type === 'billing' && (
                  <AddressFormControlLabel
                    label={<strong>Same as Shipping</strong>}
                    value={String(order.shipping_address.id)}
                    address={order.shipping_address}
                    key={order.shipping_address.id}
                  />
                )}
                {addresses.map((address, idx) =>
                  type === 'billing' &&
                  address.id === _.get(order.shipping_address, 'id') ? null : (
                    <AddressFormControlLabel
                      value={String(address.id)}
                      address={address}
                      key={address.id}
                    />
                  )
                )}
                <CheckoutFormControlLabel
                  value="new"
                  label={<NewAddressLabel>+ Add New Address</NewAddressLabel>}
                  withCollapse
                />
              </RadioGroup>
              <CheckoutCollapse
                in={selectedAddressOption === 'new'}
                style={{
                  marginBottom: selectedAddressOption === 'new' ? '60px' : '0'
                }}
              >
                <AddressForm
                  type={type}
                  orderToken={order.token}
                  orderCountries={order.destination_country_options}
                />
              </CheckoutCollapse>
              {selectedAddressOption !== 'new' && (
                <NextButton
                  id={`${type}-submit`}
                  onClick={() => goToNext()}
                  variant="contained"
                  color="primary"
                >
                  Use This Address
                </NextButton>
              )}
            </FormControl>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default connect(
  state => ({
    activeStep: state.checkout.activeStep,
    order: state.checkout.order,
    buckLoading: state.buckaneer.loading,
    checkoutLoading: state.checkout.orderLoading,
    billingAddresses: state.buckaneer.billingAddresses,
    shippingAddresses: state.buckaneer.shippingAddresses,
    lastBillingAddress: state.buckaneer.lastBillingAddress,
    lastShippingAddress: state.buckaneer.lastShippingAddress
  }),
  {
    getBillingAddresses,
    getShippingAddresses,
    getLastBillingAddress,
    getLastShippingAddress,
    updateOrderAddress
  }
)(AddressSelector);
